import { IconButton, LuIcon, Menu, radiusCva, stopPropagation, Text, useProvider } from "@overmap-ai/blocks"
import { OrganizationAccessLevel, type ProjectFile } from "@overmap-ai/common"
import { isImageFile, selectProjectFileById } from "@overmap-ai/core"
import * as RadixToggleGroup from "@radix-ui/react-toggle-group"
import * as turf from "@turf/turf"
import { cx } from "class-variance-authority"
import { LngLatBounds, type LngLatLike } from "mapbox-gl"
import { type ComponentPropsWithRef, memo, type MouseEventHandler, useCallback } from "react"
import { twMerge } from "tailwind-merge"

import { useImageOverlayPicker } from "../../../contexts/hooks"
import { useConfirmDeleteDialog, useFetchFileFromFileModel } from "../../../hooks/utils"
import { useCurrentMap } from "../../../mapbox/hooks"
import { modelIconMapping } from "../../../mappings/icons"
import { sdk } from "../../../sdk"
import { hideProjectFile, showProjectFile, useAppDispatch, useAppSelector } from "../../../store"
import { selectIsProjectFileVisible } from "../../../store/selectors"
import { OrganizationAccessRequired } from "../../OrganizationAccessRequired/AccessRequired"

export interface ProjectFileVisibilityItemProps extends ComponentPropsWithRef<"li"> {
	projectFileId: ProjectFile["uuid"]
}

export const ProjectFileVisibilityItem = memo<ProjectFileVisibilityItemProps>((props) => {
	const { ref, className, projectFileId, ...rest } = props

	const projectFile = useAppSelector(selectProjectFileById(projectFileId))
	const { radius } = useProvider()

	if (!projectFile) {
		throw new Error(`ProjectFile with id ${projectFileId} not found in store`)
	}

	const dispatch = useAppDispatch()
	const isVisible = useAppSelector(selectIsProjectFileVisible(projectFileId))
	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const openImageOverlayPicker = useImageOverlayPicker()
	const map = useCurrentMap()

	const [file] = useFetchFileFromFileModel(projectFile, sdk.projectFiles)

	const handleClick = useCallback<MouseEventHandler<HTMLLIElement>>(
		(e) => {
			if (e.defaultPrevented) return
			dispatch(isVisible ? hideProjectFile(projectFile.uuid) : showProjectFile(projectFile.uuid))
		},
		[projectFile.uuid, dispatch, isVisible],
	)

	const handleDeleteClick = useCallback(() => {
		openConfirmDeleteDialog({
			title: "Delete Overlay",
			description: "Are you sure you want to delete this overlay? This cannot be undone.",
			onAction: () => {
				void sdk.projectFiles.delete(projectFile.uuid)
			},
		})
	}, [openConfirmDeleteDialog, projectFile.uuid])

	const handleEditClick = useCallback(() => {
		if (!map || !file || !isImageFile(file)) return

		openImageOverlayPicker((close) => ({
			initialBounds: new LngLatBounds(projectFile.bounds!.coordinates as [LngLatLike, LngLatLike]),
			file: file,
			onConfirm: (bounds) => {
				close()

				void sdk.projectFiles.update({
					uuid: projectFile.uuid,
					bounds: turf.multiPoint(bounds.toArray()).geometry,
				})
			},
			onCancel: close,
		}))
	}, [projectFile.uuid, file, map, openImageOverlayPicker, projectFile.bounds])

	return (
		<RadixToggleGroup.Item value={projectFile.uuid} asChild>
			<li
				ref={ref}
				className={twMerge(
					cx(
						className,
						"hover:bg-(--base-a3) active:bg-(--base-a4) data-[state=on]:bg-(--base-a3) group flex cursor-pointer items-center gap-2 px-2 py-1 transition-[background,opacity]",
						radiusCva({ radius, maxLarge: true }),
					),
				)}
				onClick={handleClick}
				tabIndex={0}
				{...rest}
			>
				<div className="flex min-w-0 grow items-center gap-2">
					<LuIcon icon={modelIconMapping.file} />
					<Text size="sm" className="truncate">
						{projectFile.file_name}
					</Text>
				</div>
				<Menu.Root>
					<Menu.ClickTrigger>
						<IconButton
							aria-label="more"
							size="sm"
							variant="ghost"
							accentColor="base"
							onClick={stopPropagation}
						>
							<LuIcon icon={modelIconMapping.more} />
						</IconButton>
					</Menu.ClickTrigger>
					<Menu.Content size="sm">
						{file && isImageFile(file) ? (
							<Menu.Item onSelect={handleEditClick}>
								<LuIcon icon={modelIconMapping.edit} />
								Edit
							</Menu.Item>
						) : null}
						<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
							<Menu.Separator />
							<Menu.Item onSelect={handleDeleteClick}>
								<LuIcon icon={modelIconMapping.delete} />
								Delete
							</Menu.Item>
						</OrganizationAccessRequired>
					</Menu.Content>
				</Menu.Root>
			</li>
		</RadixToggleGroup.Item>
	)
})
ProjectFileVisibilityItem.displayName = "ProjectFileVisibilityItem"
